import { supabase } from '../supabase/supabaseClient.js';

/**
 * Wrapper for fetch that automatically adds Supabase JWT to Authorization header if available (frontend-safe).
 * @param {string} url - The API endpoint (relative to backend origin).
 * @param {object} options - Fetch options (method, headers, body, etc.).
 */
export async function apiFetch(url, options = {}) {
  let accessToken = null;
  const session = await supabase.auth.getSession();
  accessToken = session.data?.session?.access_token;

  const headers = {
    ...(options.headers || {}),
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    'Content-Type': 'application/json',
  };

  const baseUrl = process.env.REACT_APP_API_BASE_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '');
  // If url is a relative path, prefix with backend base URL (if provided)
  const apiUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
  const { signal, ...rest } = options;
  const fetchOptions = { ...rest, headers, signal };
  if (fetchOptions.body && typeof fetchOptions.body !== 'string') {
    fetchOptions.body = JSON.stringify(fetchOptions.body);
  }
  const response = await fetch(apiUrl, fetchOptions);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
    error.status = response.status;
    throw error;
  }

  // Try to parse the response as JSON. If the body is empty, it will return null.
  const text = await response.text();
  if (!text) {
    return null;
  }
  return JSON.parse(text);
}

// --- Public Topic Engine API (Phase 3) ---

export async function getPublicTopics(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/api/public/topics?${qs}`);
}

export async function getPublicTopic(slug) {
  return apiFetch(`/api/public/topics/${slug}`);
}

export async function getPublicInsight(id) {
  return apiFetch(`/api/public/insights/${id}`);
}

export async function getPublicBrand(slug) {
  return apiFetch(`/api/public/brands/${slug}`);
}

// --- Authenticated Insight Editor API ---

export async function getInsight(id) {
  return apiFetch(`/api/insights/${id}`);
}

export async function updateInsight(id, data) {
  return apiFetch(`/api/insights/${id}`, {
    method: 'PUT',
    body: data
  });
}

export async function publishInsight(id) {
  return apiFetch(`/api/insights/${id}/publish`, {
    method: 'POST'
  });
}

export async function getInsightsByEpisode(episodeId, { projectId } = {}) {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  return apiFetch(`/api/episodes/${episodeId}/insights${qs}`);
}

// --- Q&A API ---

export async function getQADrafts(episodeId, { projectId } = {}) {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  return apiFetch(`/api/qa/drafts/${episodeId}${qs}`);
}

export async function createOrUpdateQA(data) {
  return apiFetch('/api/qa', {
    method: 'POST',
    body: data
  });
}

export async function publishQA(id, { projectId, episodeId } = {}) {
  return createOrUpdateQA({ id, projectId, episodeId, status: 'published' });
}

export async function getPublicTopicQA(topicSlug) {
  return apiFetch(`/api/public/topics/${topicSlug}/qa`);
}

export async function getPublicInsightQA(insightId) {
  return apiFetch(`/api/public/insights/${insightId}/qa`);
}

// --- Project Helpers ---

export async function getProjectBySlug(slug) {
  return apiFetch(`/api/projects/by-slug/${slug}`);
}

export async function getProjects() {
  return apiFetch('/api/projects');
}

export async function createProject(data) {
  return apiFetch('/api/projects', {
    method: 'POST',
    body: data
  });
}

export async function updateProject(projectId, data) {
  return apiFetch(`/api/projects/${projectId}`, {
    method: 'PUT',
    body: data,
  });
}

export async function uploadProjectLogo(projectId, file) {
  if (!projectId) throw new Error('Missing projectId');
  if (!file) throw new Error('Missing file');

  const session = await supabase.auth.getSession();
  const accessToken = session.data?.session?.access_token;
  if (!accessToken) throw new Error('Not authenticated');

  const baseUrl = process.env.REACT_APP_API_BASE_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '');
  const apiUrl = `${baseUrl}/api/projects/${projectId}/logo`;

  const form = new FormData();
  form.append('file', file);

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: form,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: response.statusText }));
    const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
    error.status = response.status;
    throw error;
  }

  const text = await response.text();
  if (!text) return null;
  return JSON.parse(text);
}

export async function deleteProject(projectId) {
  return apiFetch(`/api/projects/${projectId}`, {
    method: 'DELETE',
  });
}

export async function getProjectStats(projectId) {
  return apiFetch(`/api/projects/${projectId}/stats`);
}

// --- Project Team API ---

export async function getProjectMembers(projectId) {
  return apiFetch(`/api/projects/${projectId}/members`);
}

export async function inviteProjectMember(projectId, email, role) {
  return apiFetch(`/api/projects/${projectId}/invites`, {
    method: 'POST',
    body: { email, role },
  });
}

export async function removeProjectMember(projectId, userId) {
  return apiFetch(`/api/projects/${projectId}/members/${userId}`, {
    method: 'DELETE',
  });
}

export async function revokeProjectInvite(projectId, inviteId) {
  return apiFetch(`/api/projects/${projectId}/invites/${inviteId}`, {
    method: 'DELETE',
  });
}

export async function redeemProjectInvite(token) {
  return apiFetch('/api/projects/invites/redeem', {
    method: 'POST',
    body: { token },
  });
}

export async function getInsights(params = {}) {
  const qs = new URLSearchParams(params).toString();
  return apiFetch(`/api/insights?${qs}`);
}

// --- Insight Engine v2 helpers ---
export async function generateBatch(vaultId, body) {
  return apiFetch(`/api/vaults/${vaultId}/insights/generate-batch`, {
    method: 'POST',
    body: body || {},
  });
}

export async function previewEpisode(vaultId, episodeId, opts = {}) {
  return apiFetch(`/api/vaults/${vaultId}/insights/preview`, {
    method: 'POST',
    body: { episodeId, ...opts },
  });
}

export async function getInsightsV2(vaultId) {
  return apiFetch(`/api/vaults/${vaultId}/insights/v2`);
}

export async function listInsightRuns(vaultId, params = {}) {
  const qs = new URLSearchParams(params).toString();
  const suffix = qs ? `?${qs}` : '';
  return apiFetch(`/api/vaults/${vaultId}/insight-runs${suffix}`);
}

export async function getInsightRun(vaultId, runId) {
  return apiFetch(`/api/vaults/${vaultId}/insight-runs/${runId}`);
}

// --- Episode ingestion helpers ---

export async function searchEpisodes(query, { limit = 20, signal } = {}) {
  const qs = new URLSearchParams({ query, limit: String(limit) }).toString();
  return apiFetch(`/api/public/podcast/search?${qs}`, { signal });
}

export async function resolveEpisodeUrl(url) {
  return apiFetch('/api/episodes/resolve-url', {
    method: 'POST',
    body: { url },
  });
}

export async function requestEpisodeUploadUrl({ fileName, contentType, projectId }) {
  const headers = projectId ? { 'X-Project-Id': projectId } : undefined;
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  return apiFetch(`/api/episodes/upload-url${qs}`, {
    method: 'POST',
    headers,
    body: { fileName, contentType, projectId },
  });
}

export async function importEpisode(payload, { projectId } = {}) {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  return apiFetch(`/api/episodes/import${qs}`, {
    method: 'POST',
    body: payload,
  });
}

// --- YouTube Import ---

export async function checkYouTubeAvailability() {
  return apiFetch('/api/episodes/youtube/check');
}

export async function getYouTubeMetadata(url, { projectId } = {}) {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  return apiFetch(`/api/episodes/youtube/metadata${qs}`, {
    method: 'POST',
    body: { url },
  });
}

export async function importYouTubeVideo(url, metadata = {}, { projectId } = {}) {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  return apiFetch(`/api/episodes/youtube/import${qs}`, {
    method: 'POST',
    body: { url, metadata },
  });
}

export function isYouTubeUrl(url) {
  if (!url) return false;
  const patterns = [
    /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\//i,
    /^(https?:\/\/)?(www\.)?youtube\.com\/watch\?v=/i,
    /^(https?:\/\/)?youtu\.be\//i,
    /^(https?:\/\/)?(www\.)?youtube\.com\/shorts\//i,
  ];
  return patterns.some(pattern => pattern.test(url));
}

export async function getEpisodeImportStatus(jobId, { signal, projectId } = {}) {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  return apiFetch(`/api/episodes/import/${jobId}/status${qs}`, { signal });
}

export async function reingestEpisode(episodeId, { projectId, forceTranscribe = false } = {}) {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  return apiFetch(`/api/episodes/${episodeId}/reingest${qs}`, {
    method: 'POST',
    body: { forceTranscribe },
  });
}

export async function listEpisodes({ projectId, limit } = {}) {
  const params = new URLSearchParams();
  if (projectId) params.set('projectId', projectId);
  if (limit != null) params.set('limit', String(limit));
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return apiFetch(`/api/episodes${suffix}`);
}

export async function deleteEpisode(episodeId, { projectId } = {}) {
  const params = new URLSearchParams();
  if (projectId) params.set('projectId', projectId);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return apiFetch(`/api/episodes/${episodeId}${suffix}`, {
    method: 'DELETE',
  });
}

export async function getCampaignByEpisode(episodeId, { projectId } = {}) {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  return apiFetch(`/api/campaigns/${episodeId}${qs}`);
}

export async function runCampaignForEpisode(episodeId, { projectId } = {}) {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  return apiFetch(`/api/campaigns/${episodeId}/run${qs}`, {
    method: 'POST',
  });
}

export async function generateContentFromCampaign(episodeId, body = {}, { projectId } = {}) {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  return apiFetch(`/api/campaigns/${episodeId}/content${qs}`, {
    method: 'POST',
    body: body,
  });
}

export async function getEpisodeContent(episodeId, { projectId } = {}) {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  return apiFetch(`/api/episodes/${episodeId}/content${qs}`);
}

// --- Transcript signed upload URL helper ---
export async function requestTranscriptUploadUrl({ fileName, contentType, projectId }) {
  const headers = projectId ? { 'X-Project-Id': projectId } : undefined;
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  return apiFetch(`/api/episodes/transcripts/upload-url${qs}`, {
    method: 'POST',
    headers,
    body: { fileName, contentType, projectId },
  });
}

// --- Transcript upload helper ---
export async function uploadTranscript({ projectId, title, language = 'auto', format, content, idempotencyKey }) {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  const headers = projectId ? { 'X-Project-Id': projectId } : undefined;
  return apiFetch(`/api/episodes/upload-transcript${qs}`, {
    method: 'POST',
    headers,
    body: { title, language, format, content, idempotencyKey },
  });
}

// --- Episode sharing helpers ---

export async function shareEpisodeToProject(episodeId, targetProjectId, { projectId } = {}) {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  return apiFetch(`/api/episodes/${episodeId}/share${qs}`, {
    method: 'POST',
    body: { targetProjectId },
  });
}

export async function unshareEpisodeFromProject(episodeId, targetProjectId, { projectId } = {}) {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  return apiFetch(`/api/episodes/${episodeId}/share/${targetProjectId}${qs}`, {
    method: 'DELETE',
  });
}

export async function getEpisodeSharedProjects(episodeId, { projectId } = {}) {
  const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
  return apiFetch(`/api/episodes/${episodeId}/shared-projects${qs}`);
}
